/**
 * Video Player Modal
 * Extends the base modal for embedded video playback
 * Supports YouTube, Vimeo, and direct video files
 */

import AccessibleModal from './modal.js';
import { devLog } from './utils.js';

class VideoPlayerModal extends AccessibleModal {
    constructor(options = {}) {
        const modalOptions = {
            closeOnBackdrop: true,
            closeOnEscape: true,
            focusOnOpen: true,
            animationDuration: 300,
            ...options
        };

        super('video-player-modal', modalOptions);
        
        this.currentVideoUrl = null;
        this.videoType = null;
        this.videoId = null;
        this.videoTitle = '';
        
        // Bind methods
        this.handleVideoEnd = this.handleVideoEnd.bind(this);
        this.pauseVideo = this.pauseVideo.bind(this);
        this.handleVideoError = this.handleVideoError.bind(this);
        
        devLog('Video Player Modal initialized');
    }

    /**
     * Open modal with a video URL
     * @param {string} videoUrl - The video URL to play
     * @param {string} title - Optional title for the modal
     */
    openWithVideo(videoUrl, title = 'Video Player') {
        if (!videoUrl) {
            console.error('Video URL is required');
            return;
        }

        this.videoTitle = title;
        
        if (!this.parseVideoUrl(videoUrl)) {
            this.showError('Unsupported video format', 'Please check the video URL and try again.');
            return;
        }

        this.showLoading();
        this.open('', title);
        
        // Add video-specific class to modal
        this.modal.classList.add('modal--video');
        
        // Hide the header for video modals to maximize video space
        this.hideHeader();
        
        // Generate and set video content
        setTimeout(() => {
            const content = this.generateVideoContent();
            this.setContent(content);
            this.setupVideoEvents();
            this.setInitialFocus();
        }, 100);

        devLog('Video modal opened:', { url: videoUrl, type: this.videoType, id: this.videoId });
    }

    /**
     * Parse video URL and extract platform and ID
     * @param {string} url - Video URL to parse
     * @returns {boolean} - Whether URL was successfully parsed
     */
    parseVideoUrl(url) {
        this.currentVideoUrl = url;

        // YouTube parsing - supports various YouTube URL formats
        const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
        const youtubeMatch = url.match(youtubeRegex);
        
        if (youtubeMatch) {
            this.videoType = 'youtube';
            this.videoId = youtubeMatch[1];
            return true;
        }

        // Vimeo parsing - supports vimeo.com URLs
        const vimeoRegex = /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/)?(\d+)(?:$|\/|\?)/;
        const vimeoMatch = url.match(vimeoRegex);
        
        if (vimeoMatch) {
            this.videoType = 'vimeo';
            this.videoId = vimeoMatch[3] || vimeoMatch[1];
            return true;
        }

        // Direct video file - supports common video formats
        const videoFileRegex = /\.(mp4|webm|ogg|mov|avi|wmv|flv|m4v)(\?.*)?$/i;
        if (videoFileRegex.test(url)) {
            this.videoType = 'direct';
            return true;
        }

        return false;
    }

    /**
     * Generate video content based on platform
     * @returns {string} - HTML content for the video
     */
    generateVideoContent() {
        switch (this.videoType) {
            case 'youtube':
                return this.generateYouTubeEmbed();
            case 'vimeo':
                return this.generateVimeoEmbed();
            case 'direct':
                return this.generateDirectVideoEmbed();
            default:
                return this.generateErrorContent('Unsupported video format');
        }
    }

    /**
     * Generate YouTube embed HTML
     * @returns {string} - YouTube iframe HTML
     */
    generateYouTubeEmbed() {
        const embedUrl = `https://www.youtube.com/embed/${this.videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
        
        return `
            <div class="video-modal__container">
                <div class="video-modal__player">
                    <iframe 
                        src="${embedUrl}"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowfullscreen
                        title="${this.escapeHtml(this.videoTitle)}"
                        loading="lazy">
                    </iframe>
                </div>
            </div>
        `;
    }

    /**
     * Generate Vimeo embed HTML
     * @returns {string} - Vimeo iframe HTML
     */
    generateVimeoEmbed() {
        const embedUrl = `https://player.vimeo.com/video/${this.videoId}?autoplay=1&title=0&byline=0&portrait=0&playsinline=1`;
        
        return `
            <div class="video-modal__container">
                <div class="video-modal__player">
                    <iframe 
                        src="${embedUrl}"
                        frameborder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowfullscreen
                        title="${this.escapeHtml(this.videoTitle)}">
                    </iframe>
                </div>
            </div>
        `;
    }

    /**
     * Generate direct video file HTML
     * @returns {string} - HTML5 video element
     */
    generateDirectVideoEmbed() {
        return `
            <div class="video-modal__container">
                <div class="video-modal__player">
                    <video 
                        controls 
                        autoplay 
                        preload="metadata"
                        controlsList="nodownload"
                        playsinline
                        title="${this.escapeHtml(this.videoTitle)}">
                        <source src="${this.escapeHtml(this.currentVideoUrl)}" type="video/mp4">
                        <p>Your browser doesn't support HTML5 video. <a href="${this.escapeHtml(this.currentVideoUrl)}">Download the video</a> instead.</p>
                    </video>
                </div>
            </div>
        `;
    }

    /**
     * Generate error content HTML
     * @param {string} message - Error message
     * @param {string} details - Optional error details
     * @returns {string} - Error HTML
     */
    generateErrorContent(message, details = '') {
        return `
            <div class="video-modal__container">
                <div class="video-modal__error">
                    <div class="video-modal__error-icon" aria-hidden="true">⚠️</div>
                    <div class="video-modal__error-message">${this.escapeHtml(message)}</div>
                    ${details ? `<div class="video-modal__error-details">${this.escapeHtml(details)}</div>` : ''}
                </div>
            </div>
        `;
    }

    /**
     * Show loading state
     */
    showLoading() {
        const loadingContent = `
            <div class="video-modal__container">
                <div class="video-modal__loading" role="status" aria-label="Loading video">
                    <span class="sr-only">Loading video...</span>
                </div>
            </div>
        `;
        this.setContent(loadingContent);
    }

    /**
     * Show error state
     * @param {string} message - Error message
     * @param {string} details - Optional error details
     */
    showError(message, details = '') {
        const errorContent = this.generateErrorContent(message, details);
        this.open(errorContent, 'Video Error');
        this.modal.classList.add('modal--video', 'modal--error');
    }

    /**
     * Setup video-specific event listeners
     */
    setupVideoEvents() {
        // Listen for modal close to pause video
        if (this.modal) {
            this.modal.addEventListener('modal:beforeClose', this.pauseVideo);
        }

        // Setup error handling for video elements
        const video = this.modal?.querySelector('video');
        if (video) {
            video.addEventListener('error', this.handleVideoError);
            video.addEventListener('loadedmetadata', () => {
                devLog('Video loaded successfully');
            });
        }

        // Setup iframe error handling (limited due to cross-origin restrictions)
        const iframe = this.modal?.querySelector('iframe');
        if (iframe) {
            iframe.addEventListener('load', () => {
                devLog('Video iframe loaded');
            });
        }

        // Enhanced focus trapping for iframe content
        this.setupIframeFocusTrap();
    }

    /**
     * Setup focus trapping that handles iframe focus escaping
     */
    setupIframeFocusTrap() {
        // Use focusin event to catch when focus enters iframe content
        this.focusInHandler = (e) => {
            if (!this.isOpen) return;
            
            // Check if focus moved outside the modal
            if (!this.modal.contains(e.target)) {
                e.preventDefault();
                // Return focus to the overlay close button
                if (this.overlayCloseButton) {
                    this.overlayCloseButton.focus();
                }
                devLog('Focus escaped modal, returned to close button');
            }
        };

        // Listen for focus changes on the entire document
        document.addEventListener('focusin', this.focusInHandler);
        
        // Also add a periodic check for focus escaping (fallback)
        this.focusCheckInterval = setInterval(() => {
            if (!this.isOpen) return;
            
            const activeElement = document.activeElement;
            if (activeElement && !this.modal.contains(activeElement)) {
                // Focus has escaped, bring it back
                if (this.overlayCloseButton) {
                    this.overlayCloseButton.focus();
                    devLog('Focus check: returned escaped focus to close button');
                }
            }
        }, 100); // Check every 100ms
    }

    /**
     * Cleanup iframe focus trap
     */
    cleanupIframeFocusTrap() {
        if (this.focusInHandler) {
            document.removeEventListener('focusin', this.focusInHandler);
            this.focusInHandler = null;
        }
        
        if (this.focusCheckInterval) {
            clearInterval(this.focusCheckInterval);
            this.focusCheckInterval = null;
        }
    }

    /**
     * Handle video errors
     * @param {Event} event - Error event
     */
    handleVideoError(event) {
        console.error('Video playback error:', event);
        this.showError('Video playback failed', 'There was an error loading or playing the video.');
    }

    /**
     * Pause/stop video playback
     */
    pauseVideo() {
        if (!this.modal) return;

        const iframe = this.modal.querySelector('iframe');
        const video = this.modal.querySelector('video');
        
        if (iframe) {
            // For YouTube/Vimeo, we need to reload iframe to stop playback
            // This is the most reliable cross-origin method
            const src = iframe.src;
            iframe.src = '';
            setTimeout(() => {
                if (iframe.parentNode) {
                    iframe.src = src.replace('autoplay=1', 'autoplay=0');
                }
            }, 100);
        }
        
        if (video) {
            video.pause();
            video.currentTime = 0;
        }

        devLog('Video paused/stopped');
    }

    /**
     * Handle video end event
     */
    handleVideoEnd() {
        devLog('Video playback ended');
        // Could implement auto-close or related videos here
    }

    /**
     * Override close method to ensure video stops and cleanup
     */
    close() {
        this.pauseVideo();
        this.cleanupIframeFocusTrap();
        this.modal?.classList.remove('modal--video');
        super.close();
    }

    /**
     * Override destroy method for cleanup
     */
    destroy() {
        this.pauseVideo();
        this.cleanupIframeFocusTrap();
        super.destroy();
    }

    /**
     * Utility method to escape HTML
     * @param {string} text - Text to escape
     * @returns {string} - Escaped text
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Hide the modal header and create overlay close button
     */
    hideHeader() {
        if (!this.modal) return;

        const header = this.modal.querySelector('.modal__header');
        if (header) {
            header.style.display = 'none';
        }

        // Create overlay close button
        this.createOverlayCloseButton();
    }

    /**
     * Create an overlay close button for video modals
     */
    createOverlayCloseButton() {
        if (!this.modal) return;

        // Remove existing overlay close button if it exists
        const existingOverlayClose = this.modal.querySelector('.modal__close-overlay');
        if (existingOverlayClose) {
            existingOverlayClose.remove();
        }

        // Create overlay close button
        const overlayCloseButton = document.createElement('button');
        overlayCloseButton.className = 'modal__close-overlay';
        overlayCloseButton.setAttribute('aria-label', 'Close video');
        overlayCloseButton.setAttribute('type', 'button');
        overlayCloseButton.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M10 8.586L2.929 1.515 1.515 2.929 8.586 10l-7.071 7.071 1.414 1.414L10 11.414l7.071 7.071 1.414-1.414L11.414 10l7.071-7.071-1.414-1.414L10 8.586z"/>
            </svg>
        `;

        // Add click handler
        overlayCloseButton.addEventListener('click', this.close);

        // Insert as sibling of modal container (not inside it to avoid overflow clipping)
        const modalContainer = this.modal.querySelector('.modal__container');
        if (modalContainer && modalContainer.parentNode) {
            modalContainer.parentNode.insertBefore(overlayCloseButton, modalContainer);
        }

        // Store reference for focus management
        this.overlayCloseButton = overlayCloseButton;
    }

    /**
     * Set initial focus for video modal (overlay close button)
     */
    setInitialFocus() {
        // Update focus management to include overlay close button
        this.setupFocusManagement();
        
        if (this.overlayCloseButton && this.options.focusOnOpen) {
            // Small delay to ensure modal animation is complete
            setTimeout(() => {
                this.overlayCloseButton.focus();
            }, 50);
        }
    }

    /**
     * Override focus management to include overlay close button
     */
    setupFocusManagement() {
        // Call parent method first
        super.setupFocusManagement();
        
        // Add overlay close button to focusable elements if it exists
        if (this.overlayCloseButton) {
            this.focusableElements.unshift(this.overlayCloseButton); // Add to beginning
            this.firstFocusable = this.overlayCloseButton;
            this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
        }
        
        devLog('Video modal focus management updated:', {
            focusableCount: this.focusableElements.length,
            firstFocusable: this.firstFocusable?.tagName,
            lastFocusable: this.lastFocusable?.tagName
        });
    }

    /**
     * Get video information
     * @returns {object} - Video info object
     */
    getVideoInfo() {
        return {
            url: this.currentVideoUrl,
            type: this.videoType,
            id: this.videoId,
            title: this.videoTitle,
            isOpen: this.isOpen
        };
    }
}

export default VideoPlayerModal;
